import React from "react";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { money, num, ago } from "@/lib/sk";
import { groupId, humanGroup, sectionSummary } from "@/lib/kalkyle";

export const K = (v) => `${money(v, "NOK", 1, { bare: true })} kr`;
const STAGES = [["estimert", "Estimert"], ["komplett", "Komplett"], ["kontrollert", "Kontrollert"]];
/** Row grammar shared with the list: text left, the figures in the same fixed right-hand columns, the menu gutter kept free. */
const rowBtn = "group flex w-full items-baseline gap-4 border-t border-black/[0.06] py-[11px] text-left outline-none transition-colors first:border-t-0 hover:bg-black/[0.02] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30 -mx-2 px-2 rounded-[8px] sm:pr-[50px]";

export function Title({ children, aside }) {
  return <div className="flex items-baseline justify-between gap-3"><h3 className="k-label">{children}</h3>{aside}</div>;
}

/** Estimert · Komplett · Kontrollert – where the calculation stands as a whole, said once. */
export function Stage({ stage, quote }) {
  const reached = stage === "start" ? -1 : STAGES.findIndex(([k]) => k === stage);
  return (
    <ol className="flex flex-wrap items-center gap-x-2 text-[13px]" data-testid="kalkyle-stage" data-stage={stage}>
      {STAGES.map(([k, label], i) => (
        <li key={k} className="flex items-center gap-2">
          {i > 0 ? <span className="h-px w-5 bg-black/[0.12]" aria-hidden /> : null}
          <span className={cn("inline-flex items-center gap-1.5", i === reached ? "font-semibold text-foreground" : i < reached ? "text-foreground/70" : "text-muted-foreground/70")} data-testid={`kalkyle-stage-${k}`}>
            {i < reached ? <Check className="h-3.5 w-3.5 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> : <span className={cn("h-[7px] w-[7px] rounded-full", i === reached ? "bg-[hsl(var(--brand))]" : "bg-black/[0.14]")} aria-hidden />}
            {label}{k === "kontrollert" && i === reached && quote?.revision ? <span className="font-normal text-muted-foreground"> · versjon {quote.revision}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** The one economic headline: the fixed-price sum when it exists, the honest qualifier beneath – never a zero. */
export function Hero({ ov, totals: t }) {
  const sub = ov.stage === "estimert" ? `foreløpig – ${t.missing_count} ${t.missing_count === 1 ? "post" : "poster"} uten komplett pris` : ov.stage === "start" ? "ingen poster i fastpris er priset ennå" : ov.stage === "komplett" ? "alle poster er ferdig" : "komplett og kontrollert";
  return (
    <div className="mt-5" data-testid="kalkyle-sum" data-has-sum={ov.hasSum ? "1" : "0"}>
      <div className="k-meta">Fastprissum{ov.hasSum && ov.preliminary ? " · foreløpig" : ""}</div>
      <div className="k-hero mt-1.5 text-foreground">{ov.hasSum ? K(t.price) : "–"}</div>
      <div className="k-meta mt-1.5">{sub}</div>
    </div>
  );
}

/** Kost · Dekningsbidrag · Timer · Underleverandør as one line of type – no cards, dashes where nothing is known. */
export function FiguresLine({ ov, totals: t }) {
  const db = ov.hasSum ? t.price - t.cost : null;
  const cells = [
    ["Kost", ov.hasSum ? K(t.cost) : "–", ov.hasSum && t.cost_is_simulated ? "delvis simulert" : null, "kalkyle-figure-cost"],
    ["Dekningsbidrag", db != null ? K(db) : "–", db != null && t.db_pct != null ? `${num(t.db_pct, 1)} %` : null, "kalkyle-figure-db"],
    ["Timer", t.hours ? num(t.hours, 0) : "–", null, "kalkyle-figure-hours"],
    ["Underleverandør", ov.hasSum && t.sub_share_pct != null && t.sub_price ? `${num(t.sub_share_pct, 0)} %` : "–", ov.hasSum && t.sub_price ? K(t.sub_price) : null, "kalkyle-figure-sub"],
  ];
  return (
    <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-3" data-testid="kalkyle-figures">
      {cells.map(([label, value, sub, id]) => (
        <div key={id} className="min-w-0" data-testid={id}>
          <dt className="k-meta">{label}</dt>
          <dd className="mt-0.5 flex items-baseline gap-2"><span className="k-figure text-foreground">{value}</span>{sub ? <span className="k-meta">{sub}</span> : null}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * «4 av 8 poster ferdig» with a thin bar – ferdig is the measure. A post with an amount that is not yet assessed is
 * said as exactly that, so a priced calculation is never mistaken for a finished one.
 */
export function Progress({ ov, totals, onItems }) {
  const st = ov.byState || {};
  const pct = ov.total ? Math.round((ov.handled / ov.total) * 100) : 0;
  const pricedOpen = Math.max(0, (ov.priced || 0) - (st.klar || 0));
  const parts = [["klar", st.klar, "ferdig", "klar"], ["venter", st.venter_pris, "venter på pris", "uferdige"], ["vurder", st.ma_vurderes, "ikke vurdert ferdig", "uferdige"], ["inkl", st.inkludert, "inkludert", "klar"], ["utgar", st.utgar, "utgår", "klar"]].filter(([, n]) => n);
  return (
    <div data-testid="kalkyle-progress-section">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[15px] font-medium text-foreground" data-testid="kalkyle-progress" data-kind={totals.sum_kind} data-done={ov.handled} data-priced={ov.priced}>{ov.handled} av {ov.total} poster ferdig</span>
        <span className="k-meta tabular">{pct} %</span>
      </div>
      <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-black/[0.06]"><div className="h-full rounded-full bg-[hsl(var(--brand))] transition-[width] duration-300" style={{ width: `${pct}%` }} /></div>
      <p className="k-meta mt-2 flex flex-wrap gap-x-1">
        {parts.map(([k, n, label, f], i) => <button key={k} type="button" onClick={() => onItems(f)} className="hover:text-foreground hover:underline" data-testid={`kalkyle-state-${k}`}>{i ? "· " : ""}{n} {label}</button>)}
      </p>
      {pricedOpen ? <p className="k-meta mt-1" data-testid="kalkyle-priced-open">{pricedOpen} {pricedOpen === 1 ? "post har beløp, men er ikke vurdert ferdig" : "poster har beløp, men er ikke vurdert ferdig"}</p> : null}
    </div>
  );
}

/** Economy per section as one compact table – Fagområde · Fremdrift · DB % · Salg. A dash is never a zero. Click opens the section in the list. */
export function GroupsTable({ grouped, onGroup }) {
  if (grouped.length < 2 && !grouped[0]?.key) return null;
  return (
    <section className="mt-10" data-testid="kalkyle-groups">
      <div className="flex items-baseline gap-4 sm:pr-[50px]">
        <h3 className="k-label min-w-0 flex-1">Gruppe</h3>
        <span className="k-label w-[88px] shrink-0 text-right">Fremdrift</span>
        <span className="k-label hidden w-[72px] shrink-0 text-right sm:inline">DB %</span>
        <span className="k-label w-[128px] shrink-0 text-right">Salg</span>
      </div>
      <div className="mt-1">
        {grouped.map((g) => {
          const s = sectionSummary(g.rows);
          return (
            <button key={g.key || "alle"} type="button" onClick={() => onGroup(g.key)} className={rowBtn} data-testid={`kalkyle-group-${groupId(g.key)}`} data-done={s.done ? "1" : "0"}>
              <span className="flex min-w-0 flex-1 items-baseline gap-2"><span className="k-title truncate text-foreground">{humanGroup(g.label)}</span>{s.done ? <Check className="h-3.5 w-3.5 shrink-0 translate-y-[2px] text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> : null}{s.issues ? <span className="k-meta shrink-0 !text-[hsl(var(--v-assumed))]">{s.issues} avvik</span> : null}</span>
              <span className={cn("tabular w-[88px] shrink-0 text-right text-[14px]", s.done ? "text-foreground" : "text-muted-foreground")}>{s.doneCount} av {s.total}</span>
              <span className="k-meta tabular hidden w-[72px] shrink-0 text-right sm:inline">{s.any && s.dbPct != null ? `${num(s.dbPct, 0)} %` : "–"}</span>
              <span className={cn("k-amount w-[128px] shrink-0 text-right", s.any ? "text-foreground" : "text-muted-foreground/60")}>{s.any ? K(s.price) : "–"}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** Posts billed by the hour, options and per-unit prices stand beside the fixed-price sum – never inside it, never as zero. */
export function Outside({ ov, onItems }) {
  const rows = [["regning", ov.outside.regning, "etter regning", "faktureres etter medgått tid", "regning"], ["opsjon", ov.outside.opsjon, "som opsjon", "tilbys som tillegg", "opsjon"], ["enhet", ov.outside.enhet, "med enhetspris uten mengde", "mengden avregnes", "uferdige"]].filter(([, x]) => x.rows.length);
  if (!rows.length) return null;
  return (
    <section className="mt-10" data-testid="kalkyle-outside">
      <Title>Utenfor fastprissummen</Title>
      <div className="mt-1">
        {rows.map(([k, x, label, hint, f]) => (
          <button key={k} type="button" onClick={() => onItems(f)} className={rowBtn} data-testid={`kalkyle-outside-${k}`}>
            <span className="min-w-0 flex-1"><span className="k-title block text-foreground">{x.rows.length} {x.rows.length === 1 ? "post" : "poster"} {label}</span><span className="k-meta block">{hint}</span></span>
            <span className={cn("k-amount w-[128px] shrink-0 text-right", x.sum != null ? "text-foreground" : "text-muted-foreground/60")}>{x.sum != null ? <>{k === "regning" ? <span className="k-meta">anslag </span> : null}{K(x.sum)}</> : "–"}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** One line in «Trenger oppmerksomhet»: what, a quiet qualifier, and the way in on hover. */
export function Line({ title, sub, action, onClick, tone, testId, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={cn("group -mx-2 flex w-full items-baseline gap-3 rounded-[8px] px-2 py-2.5 text-left outline-none", onClick && "transition-colors hover:bg-black/[0.02] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30")} data-testid={testId}>
      <span className="min-w-0 flex-1">
        <span className={cn("block text-[15px] leading-[1.4]", tone === "warn" ? "text-[hsl(var(--v-assumed))]" : tone === "quiet" ? "text-foreground/75" : "text-foreground")}>{title}</span>
        {sub ? <span className="k-meta block">{sub}</span> : null}
        {children}
      </span>
      {onClick ? <span className="k-meta shrink-0 opacity-0 transition-opacity group-hover:opacity-100">{action || "Vis"} <ArrowRight className="inline h-3.5 w-3.5" /></span> : null}
    </Tag>
  );
}

/** The latest frozen revision against today's calculation – «endret siden» with the honest delta when both sums exist. */
export function Revision({ quote, ov, totals, onQuote, onDiff }) {
  const complete = totals.sum_kind === "complete";
  return (
    <section className="mt-10" data-testid="kalkyle-revision">
      <Title aside={<button type="button" onClick={onQuote} className="text-[13px] font-medium text-[hsl(var(--brand-strong))] hover:underline" data-testid="kalkyle-revision-open">Åpne tilbudet</button>}>Tilbud</Title>
      {!quote ? (
        <p className="mt-2 text-[15px] text-muted-foreground" data-testid="kalkyle-revision-none">{complete ? "Kalkylen er komplett – ingen tilbudsversjon er laget ennå." : "Ingen tilbudsversjon ennå. Den lages når kalkylen er komplett."}</p>
      ) : (
        <div className="mt-2 text-[15px] leading-[1.55]" data-testid="kalkyle-revision-line" data-stale={quote.stale ? "1" : "0"}>
          <p className="text-foreground">Versjon {quote.revision} · {quote.status === "kontrollert" ? "kontrollert" : "utkast"}{quote.totals_price != null ? <> · <span className="tabular">{K(quote.totals_price)}</span></> : null} <span className="text-muted-foreground">· {ago(quote.created_at)}</span>{quote.count > 1 ? <span className="text-muted-foreground"> · {quote.count} versjoner</span> : null}</p>
          {quote.stale ? <p className="text-muted-foreground">Kalkylen er endret etter versjon {quote.revision}{ov.hasSum && quote.totals_price != null && totals.price !== quote.totals_price ? <> – nå <span className="tabular text-foreground">{K(totals.price)}</span> ({totals.price > quote.totals_price ? "+" : "−"}{K(Math.abs(totals.price - quote.totals_price))}{totals.sum_kind === "preliminary" ? ", foreløpig" : ""})</> : ""}{onDiff ? <>. <button type="button" onClick={() => onDiff(quote.revision, "live")} className="font-medium text-[hsl(var(--brand-strong))] hover:underline" data-testid="kalkyle-revision-diff">Se endringene</button></> : "."}</p> : <p className="text-muted-foreground">Versjonen speiler kalkylen slik den står nå.{quote.count > 1 && onDiff ? <> <button type="button" onClick={() => onDiff(quote.revision - 1, quote.revision)} className="font-medium text-[hsl(var(--brand-strong))] hover:underline" data-testid="kalkyle-revision-diff-prev">Hva endret seg fra versjon {quote.revision - 1}</button></> : null}</p>}
        </div>
      )}
    </section>
  );
}
